/**
 * Semana ISO 8601 no formato `2026-W43` — a unidade de trabalho da RAS.
 *
 * Mesmo algoritmo do protótipo (`isoWeek`/`weekRange` do HTML original) e do
 * banco (`to_char(d, 'IYYY-"W"IW')`). Datas entram como `YYYY-MM-DD` (dia civil
 * em America/Sao_Paulo); nada aqui depende do fuso da máquina.
 */

export type WeekKey = `${number}-W${string}`

const WEEK_RE = /^(\d{4})-W(\d{2})$/
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const DAY_MS = 86_400_000

export function isWeekKey(value: string): value is WeekKey {
  const m = WEEK_RE.exec(value)
  if (!m) return false
  const w = Number(m[2])
  return w >= 1 && w <= 53
}

function parseDate(iso: string): Date {
  const m = DATE_RE.exec(iso)
  if (!m) throw new Error(`Data inválida: ${iso}`)
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** Semana ISO de uma data `YYYY-MM-DD`. */
export function isoWeekOf(isoDate: string): WeekKey {
  const d = parseDate(isoDate)
  const day = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - day)
  const year = d.getUTCFullYear()
  const week = Math.ceil(((d.getTime() - Date.UTC(year, 0, 1)) / DAY_MS + 1) / 7)
  return `${year}-W${String(week).padStart(2, '0')}`
}

/** Segunda e sexta (dias úteis da RAS) da semana, em `YYYY-MM-DD`. */
export function weekRange(week: string): { monday: string; friday: string; sunday: string } {
  const m = WEEK_RE.exec(week)
  if (!m) throw new Error(`Semana inválida: ${week}`)
  const year = Number(m[1])
  const w = Number(m[2])
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const day = jan4.getUTCDay() || 7
  const monday = new Date(jan4)
  monday.setUTCDate(jan4.getUTCDate() - day + 1 + (w - 1) * 7)
  const friday = new Date(monday)
  friday.setUTCDate(monday.getUTCDate() + 4)
  const sunday = new Date(monday)
  sunday.setUTCDate(monday.getUTCDate() + 6)
  return { monday: toIsoDate(monday), friday: toIsoDate(friday), sunday: toIsoDate(sunday) }
}

export function shiftWeek(week: string, n: number): WeekKey {
  const { monday } = weekRange(week)
  const d = parseDate(monday)
  d.setUTCDate(d.getUTCDate() + 7 * n)
  return isoWeekOf(toIsoDate(d))
}

export function weekNumber(week: string): number {
  const m = WEEK_RE.exec(week)
  return m ? Number(m[2]) : 0
}

function ddmm(iso: string, withYear = false): string {
  const [y, mo, d] = iso.split('-')
  return withYear ? `${d}/${mo}/${y}` : `${d}/${mo}`
}

/** "Semana 43 · 19/10 a 23/10/2026" — mesmo texto do protótipo. */
export function weekLabel(week: string): string {
  const { monday, friday } = weekRange(week)
  return `Semana ${weekNumber(week)} · ${ddmm(monday)} a ${ddmm(friday, true)}`
}

/** Comparação de chaves de semana (a forma `YYYY-Www` ordena como texto). */
export function compareWeek(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** Hoje em America/Sao_Paulo, `YYYY-MM-DD`. */
export function todayInSaoPaulo(now: Date = new Date()): string {
  // en-CA formata como YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

export function currentWeek(now: Date = new Date()): WeekKey {
  return isoWeekOf(todayInSaoPaulo(now))
}

/** Diferença em dias corridos `b - a` entre duas datas `YYYY-MM-DD`. */
export function daysBetween(a: string, b: string): number {
  return Math.round((parseDate(b).getTime() - parseDate(a).getTime()) / DAY_MS)
}

export function addDays(iso: string, n: number): string {
  const d = parseDate(iso)
  d.setUTCDate(d.getUTCDate() + n)
  return toIsoDate(d)
}

/** Mês (`YYYY-MM`) de uma data `YYYY-MM-DD`. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7)
}

export function shiftMonth(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  const d = new Date(Date.UTC(y, m - 1 + n, 1))
  return toIsoDate(d).slice(0, 7)
}

export const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
] as const

export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number]
  return `${MONTH_NAMES[m - 1] ?? ''} ${y}`
}
